// Flow Import Modal (PRD §1.9, WP-08).
// Allows importing a flow configuration from uploaded JSON or pasted text.
import { useId, useRef, useState } from 'react';
import { useI18n } from '../../lib/i18n';
import { useDialogA11y } from '../../lib/useDialogA11y';
import { Button } from '../ui/Button';
import { CloseIcon, UploadIcon } from '../icons';
import { flowImport, type FlowDoc } from '../../lib/flows';
import { useToast } from '../ui/Toast';

interface ImportFlowModalProps {
  open: boolean;
  onImportSuccess: (flow: FlowDoc) => void;
  onClose: () => void;
}

export default function ImportFlowModal({
  open,
  onImportSuccess,
  onClose,
}: ImportFlowModalProps) {
  const { t } = useI18n();
  const toast = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [jsonText, setJsonText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const titleId = useId();
  useDialogA11y(open, panelRef, onClose);

  if (!open) return null;

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError(null);
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        setJsonText(content);
      } catch (err: any) {
        setError(err.message || 'Failed to read file');
      }
    };
    reader.onerror = () => {
      setError('Failed to read file');
    };
    reader.readAsText(file);
  };

  const handleImport = async () => {
    if (!jsonText.trim()) {
      setError(t('flow.import.errorEmpty'));
      return;
    }

    let parsed: any;
    try {
      parsed = JSON.parse(jsonText);
    } catch {
      setError(t('flow.import.errorInvalidJson'));
      return;
    }

    if (!parsed || typeof parsed !== 'object' || !Array.isArray(parsed.nodes)) {
      setError(t('flow.import.errorInvalidStructure'));
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const res = await flowImport({ config: parsed });
      if (res.data.ok && res.data.flow) {
        toast.success(t('flow.import.success'));
        onImportSuccess(res.data.flow);
        onClose();
      } else {
        setError('Import failed');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to import flow');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="w-full max-w-xl rounded-2xl border border-line bg-surface p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900"
      >
        <div className="flex items-center justify-between border-b border-line pb-4 dark:border-slate-800">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-brand/10 text-brand">
              <UploadIcon className="h-5 w-5" />
            </div>
            <div>
              <h3 id={titleId} className="text-base font-bold text-ink dark:text-white">{t('flow.import.title')}</h3>
              <p className="text-xs text-ink-3 dark:text-slate-400">{t('flow.import.subtitle')}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="rounded-lg p-1.5 text-ink-3 hover:bg-slate-100 hover:text-ink dark:hover:bg-slate-800 dark:hover:text-white"
          >
            <CloseIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="my-5 space-y-4">
          {error && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-xs text-rose-700 dark:border-rose-900/50 dark:bg-rose-950/30 dark:text-rose-400">
              {error}
            </div>
          )}

          {/* File Picker */}
          <div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".json,application/json"
              onChange={handleFileUpload}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex w-full items-center justify-center gap-2 rounded-xl border border-dashed border-line bg-surface/50 p-4 text-xs font-semibold text-ink-2 hover:border-brand hover:bg-brand/5 dark:border-slate-700 dark:bg-slate-800/50 dark:text-slate-300"
            >
              <UploadIcon className="h-4 w-4 text-brand" />
              <span>{t('flow.import.chooseFile')}</span>
            </button>
          </div>

          {/* Raw JSON input */}
          <div>
            <label className="block text-xs font-medium text-ink-2 dark:text-slate-300 mb-1.5">
              {t('flow.import.pasteJson')}
            </label>
            <textarea
              value={jsonText}
              onChange={(e) => setJsonText(e.target.value)}
              rows={8}
              placeholder='{ "name": "Support Flow", "nodes": [...], "edges": [...] }'
              className="w-full font-mono text-xs rounded-xl border border-line bg-white p-3 text-ink outline-none focus:border-brand dark:border-slate-800 dark:bg-slate-950 dark:text-slate-200"
            />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3 border-t border-line pt-4 dark:border-slate-800">
          <Button variant="ghost" size="sm" onClick={onClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleImport}
            disabled={loading || !jsonText.trim()}
          >
            {loading ? t('flow.import.importing') : t('flow.import.action')}
          </Button>
        </div>
      </div>
    </div>
  );
}
